// reCAPTCHA token helper

"use strict";

import { useReCaptcha } from "vue-recaptcha-v3";

export type RecaptchaExecutor = {
    recaptchaLoaded: () => Promise<boolean>;
    executeRecaptcha: (action: string) => Promise<string>;
};

/**
 * Requests a captcha token for the configured captcha service.
 * @param subject Action name passed to reCAPTCHA
 * @param recaptcha Optional reCAPTCHA executor; required when the service is enabled
 * @returns Captcha token, or an empty string if unavailable or on error
 */
export async function getCaptchaToken(subject: string, recaptcha?: RecaptchaExecutor | null): Promise<string> {
    try {
        switch (import.meta.env.VITE__CAPTCHA_SERVICE) {
            case "reCAPTCHA": {
                if (!recaptcha) {
                    return "";
                }
                await recaptcha.recaptchaLoaded();
                return await recaptcha.executeRecaptcha(subject);
            }
            default:
                return "";
        }
    } catch (ex) {
        console.error(ex);
        return "";
    }
}

/**
 * Composable that exposes captcha token retrieval for the current component.
 * @returns `getCaptcha` to obtain a token for a given action
 */
export function useCaptcha() {
    const recaptchaInstance = import.meta.env.VITE__CAPTCHA_SERVICE === "reCAPTCHA" ? useReCaptcha() : null;

    /**
     * Gets a captcha token for the given action.
     * @param subject Action name passed to reCAPTCHA
     */
    const getCaptcha = (subject: string) => {
        return getCaptchaToken(subject, recaptchaInstance);
    };

    return { getCaptcha };
}
